/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Family Plan — who shares this household, and who eats from it.
 *
 * Ported from MoveHelper's HouseholdView, which does this better than a row
 * buried in Settings: a real page header, a two-column card layout, roles as
 * pills, the invite code shown big and monospace with a copy button and its
 * expiry stated, and a join box whose placeholder shows the code's shape.
 *
 * ── ONE THING MOVEHELPER DOESN'T NEED ───────────────────────────────────────
 * In a meal planner "family" means two different things, and conflating them
 * causes real confusion. MEMBERS are accounts that can sign in and edit. EATERS
 * are diet profiles — a toddler who eats half a portion and is allergic to
 * peanuts is an eater, not a member, and will never have a login. This screen
 * shows both, side by side, because the question "who is this plan for?" is
 * answered by the second list, not the first.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { HouseholdApi, SettingsApi } from "../../api/endpoints.ts";
import type { DietProfile, Household } from "../../api/types.ts";
import { Loading, PageHeader, Pill, Spinner } from "../../components/ui.tsx";
import { Icon, IconButton } from "../../components/Icon.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import { SharedRecipesPanel } from "./SharedRecipesPanel.tsx";

export function FamilyView() {
  const { t } = usePrefs();
  const toast = useToast();

  const [household, setHousehold] = useState<Household | null>(null);
  const [diets, setDiets] = useState<DietProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([HouseholdApi.get(), SettingsApi.get()]);
      setHousehold(h);
      setDiets(s.diets);
      setDisplayName(h.members.find((m) => m.isSelf)?.displayName ?? "");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't load your household.", "danger");
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast(ok, "ok");
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work.", "danger");
    }
    setBusy(null);
  };

  if (loading || !household) return <Loading label={t("family.loading")} />;

  const isOwner = household.role === "owner";
  const allergens = [...new Set(diets.flatMap((d) => d.allergens))];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title={t("family.title")}
        subtitle={t("family.subtitle")}
        actions={<Pill tone="accent">{`${household.members.length} ${t("family.members")}`}</Pill>}
      />

      <div className="grid gap-4 md:grid-cols-2">
        {/* ── Members ─────────────────────────────────────────────────── */}
        <section className="card p-5">
          <div className="label-mono mb-3">
            {t("family.members")} · {household.members.length}
          </div>
          <ul className="space-y-2">
            {household.members.map((m) => (
              <li key={m.userSub} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm text-ink">
                  {/* Was 👑 / 🙂 — a crown and a smiley carrying role information
                      that the Pill beside them already states in words. The
                      emoji added no meaning, rendered differently per OS, and
                      implied a cheerfulness the data doesn't have. */}
                  <Icon name={m.role === "owner" ? "crown" : "person"}
                        className="h-4 w-4 shrink-0 text-ink-3" />
                  {m.displayName || (m.isSelf ? t("family.you") : t("family.member"))}
                  {m.isSelf && <span className="text-xs text-ink-3">({t("family.youShort")})</span>}
                </span>
                <div className="flex items-center gap-1">
                  <Pill tone={m.role === "owner" ? "accent" : "neutral"}>{m.role}</Pill>
                  {isOwner && !m.isSelf && (
                    <IconButton
                      name="x"
                      tone="danger"
                      label={t("family.removeMember")}
                      onClick={() => void run(`rm-${m.userSub}`,
                        () => HouseholdApi.removeMember(m.userSub), t("family.removed"))}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 border-t border-rule-soft pt-4">
            <div className="label-mono mb-2">{t("family.yourName")}</div>
            <div className="flex gap-2">
              <input className="input" placeholder={t("family.namePlaceholder")}
                     value={displayName}
                     onChange={(e) => setDisplayName(e.target.value)} />
              <button className="btn-primary shrink-0"
                      disabled={busy === "name" || !displayName.trim()}
                      onClick={() => void run("name",
                        () => HouseholdApi.setName(displayName.trim()), t("family.nameSaved"))}>
                {busy === "name" ? <Spinner className="h-4 w-4" /> : t("common.save")}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-ink-3">{t("family.nameHint")}</p>
          </div>
        </section>

        {/* ── Invite / join ───────────────────────────────────────────── */}
        <section className="card p-5">
          <div className="label-mono mb-2">{t("family.invite")}</div>
          <button className="btn-primary" disabled={busy === "invite"}
                  onClick={() => void run("invite", async () => {
                    setInvite((await HouseholdApi.invite()).code);
                  })}>
            {busy === "invite" ? <Spinner className="h-4 w-4" /> : t("family.generateCode")}
          </button>

          {invite && (
            <div className="mt-3 rounded-lg border border-rule-soft bg-paper p-3 text-center">
              <div className="font-mono text-lg tracking-[0.2em] text-ink">{invite}</div>
              <button className="mt-1 text-xs text-accent hover:underline"
                      onClick={() => {
                        void navigator.clipboard.writeText(invite);
                        toast(t("family.copied"), "ok");
                      }}>
                {t("family.copyExpires")}
              </button>
            </div>
          )}
          <p className="mt-2 text-xs text-ink-3">{t("family.inviteHint")}</p>

          <div className="mt-5 border-t border-rule-soft pt-4">
            <div className="label-mono mb-2">{t("family.join")}</div>
            <div className="flex gap-2">
              {/* The placeholder shows the code's actual shape — four
                  characters, a hyphen, four more. Guessing at the format is a
                  real source of failed joins. */}
              <input className="input font-mono uppercase tracking-widest"
                     placeholder="XXXX-XXXX"
                     value={joinCode}
                     onChange={(e) => setJoinCode(e.target.value.toUpperCase())} />
              <button className="btn-ghost shrink-0"
                      disabled={busy === "join" || joinCode.trim().length < 4}
                      onClick={() => void run("join", async () => {
                        await HouseholdApi.join(joinCode.trim());
                        setJoinCode("");
                      }, t("family.joined"))}>
                {busy === "join" ? <Spinner className="h-4 w-4" /> : t("family.joinButton")}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-ink-3">{t("family.joinHint")}</p>
          </div>
        </section>

        {/* ── Recipes from other families ─────────────────────────────── */}
        {/* Sits on this page rather than under Recipes because it belongs to
            the same mental category as invites: this is the screen about
            other people. The allergen set is passed down so the preview can
            warn BEFORE anything is written to the library. */}
        <SharedRecipesPanel householdAllergens={allergens} />

        {/* ── Who's eating ────────────────────────────────────────────── */}
        <section className="card p-5 md:col-span-2">
          <div className="mb-3 flex items-baseline justify-between">
            <div className="label-mono">{t("family.eaters")}</div>
            <Link to="/settings" className="text-xs text-accent hover:underline">
              {t("family.manageEaters")}
            </Link>
          </div>

          {diets.length === 0 ? (
            <p className="text-sm text-ink-3">
              {t("family.noEaters")}{" "}
              <Link to="/settings" className="text-accent hover:underline">
                {t("family.addEater")}
              </Link>
            </p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {diets.map((d) => (
                <li key={d.id} className="rounded-lg border border-rule-soft p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-ink">{d.name}</span>
                    <span className="text-xs text-ink-3">
                      {d.counts_as_servings}× {t("family.portion")}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {d.allergens.map((a) => (
                      <Pill key={a} tone="danger">{a}</Pill>
                    ))}
                    {d.diet_tags.map((tag) => (
                      <Pill key={tag} tone="neutral">{tag}</Pill>
                    ))}
                    {d.allergens.length === 0 && d.diet_tags.length === 0 && (
                      <span className="text-xs text-ink-3">{t("family.noRestrictions")}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {allergens.length > 0 && (
            // Restated at household level because this is the set the server
            // enforces against every generated recipe — not per person, but as
            // one combined list.
            <p className="mt-3 border-t border-rule-soft pt-3 text-xs text-ink-3">
              {t("family.enforced")} <strong className="text-danger">{allergens.join(", ")}</strong>
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
